import * as React from 'react';

import type { IconDefinition } from '../util';
import { BadgeContent } from './BadgeContent';
import { useBadge } from './useBadge';

export type BadgeKind =
    | 'primary'
    | 'secondary'
    | 'outlined'
    | 'info'
    | 'danger'
    | 'warning'
    | 'merge'
    | 'upgrade'
    | 'success'
    | 'new'
    | 'beta'
    | 'alpha'
    | 'basic'
    | 'premium'
    | 'ultimate'
    | 'downgraded';

export type BadgeSize = 'xxsmall' | 'xsmall' | 'small' | 'medium';
/** Label font weights available for badges that need to tune emphasis by context. */
export type BadgeWeight = 'regular' | 'medium' | 'semibold';

export interface BadgeProps extends Omit<React.ComponentPropsWithoutRef<'div'>, 'onClick'> {
    /** Optional leading icon. Size is derived from `size`. */
    icon?: IconDefinition;
    /** Flip light/dark appearance; used on dark/inverted surfaces. */
    inverted?: boolean;
    /** Hide the label visually, keep it available for assistive tech via `aria-label`. */
    iconOnly?: boolean;
    /** Semantic kind; drives color scheme. Defaults to `primary`. */
    kind?: BadgeKind;
    /** The label to show. Pass a number for nice digit formatting (handles 1 vs 123 spacing). */
    label?: React.ReactNode;
    /** Visual density. `xxsmall`/`xsmall` for counts inline with body text; `medium` for prominent call-outs. */
    size?: BadgeSize;
    /** Optional label font weight override. Plan and feature badges default to `semibold`. */
    weight?: BadgeWeight;
    /** Slimmer vertical padding variant for inline contexts. */
    thin?: boolean;
    /** Transparent background so the badge blends with its surroundings (e.g., inside a button). */
    blend?: boolean;
}

/**
 * Decorative indicator for numerical, text, or iconical state. Badges are **non-interactive** —
 * they have no hover, focus, or press affordance. Render a {@link BadgeLink} for linkable badges.
 *
 * Differs from {@link Status}, which is an inline pill with a semantic dot and supports press / action.
 * Differs from {@link Button} with `badge` prop, where the badge decorates another interactive element.
 */
export function Badge({ ref, ...props }: BadgeProps & { ref?: React.Ref<HTMLDivElement> }) {
    const { className, icon, iconOnly, inverted, kind, label, size, thin, blend, weight, ...rest } =
        props;
    const { badgeProps, iconSize, isIconOnly, isNumber } = useBadge({
        className,
        icon,
        iconOnly,
        inverted,
        kind,
        label,
        size,
        thin,
        blend,
    });

    return (
        <div ref={ref} {...badgeProps} {...rest}>
            <BadgeContent
                {...props}
                kind={kind ?? 'primary'}
                iconSize={iconSize}
                isIconOnly={isIconOnly}
                isNumber={isNumber}
                weight={weight}
            />
        </div>
    );
}
